import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { RefreshCw, Undo2, GitBranch, History, Clock, Layers, Package, ChevronDown, ChevronRight, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import AuthDialog from './AuthDialog';
import POBranchPropagateMatrix from './POBranchPropagateMatrix';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

/**
 * PropagationHistoryPanel — 2-level grouping
 *
 * Level 1 (PO / source): all products propagated from the same PO reference
 *   → "Undo all from this PO" — reverts every product in one click
 *   → "Re-propagate" — reopens the Branch Matrix pre-filled with this PO's products
 * Level 2 (product batch): one product → N branches  
 *   → "Undo set" — reverts that product across all branches
 * Level 3 (entry): individual branch row
 *   → "Undo" — single branch revert
 *
 * Non-PO propagations (SmartPropagation / manual) group under their source_ref
 * or fall into a "Manual" bucket.
 */
export default function PropagationHistoryPanel() {
  const { branches, user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const [branchId, setBranchId] = useState('__all__');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  // Track which PO groups are expanded (default: all expanded)
  const [collapsed, setCollapsed] = useState({});
  // Re-propagate dialog state
  const [repropDialog, setRepropDialog] = useState(null); // { ref, sourceBranchId, reportProducts }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId !== '__all__') params.branch_id = branchId;
      const res = await api.get('/branch-prices/propagation-history', { params });
      setEntries(res.data.entries || []);
    } catch {
      toast.error('Failed to load propagation history');
    }
    setLoading(false);
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (action) => {
    try {
      if (action.type === 'one') {
        const r = await api.post(`/branch-prices/propagation/${action.changeId}/undo`);
        if (r.data.status === 'skipped') toast.warning(r.data.reason || 'Skipped — superseded by a newer change');
        else toast.success(`Reverted to ${formatPHP(r.data.restored_capital)}`);
      } else if (action.type === 'batch') {
        const r = await api.post('/branch-prices/propagation/undo-window', { batch_id: action.batchId });
        toast.success(r.data.summary || 'Product set reverted');
      } else if (action.type === 'po') {
        // Undo ALL products propagated from this PO reference in one shot
        const r = await api.post('/branch-prices/propagation/undo-window', { source_ref: action.sourceRef });
        toast.success(r.data.summary || `All propagations from ${action.sourceRef} reverted`);
      } else {
        const body = { since: action.since };
        if (branchId !== '__all__') body.branch_id = branchId;
        const r = await api.post('/branch-prices/propagation/undo-window', body);
        toast.success(r.data.summary || 'Reverted');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    }
  };

  const gate = (action) => { setPendingAction(action); setAuthOpen(true); };
  const sinceToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); };
  const sinceLastHour = () => new Date(Date.now() - 3600 * 1000).toISOString();

  /**
   * Build the re-propagate dialog data from history entries.
   * Deduplicates by product_id (keep highest base_capital entry per product —
   * handles the rare case where a product was propagated twice from the same PO).
   * Uses base_capital (the price actually pushed) as po_unit_price so the matrix
   * shows exactly what was sent before.
   */
  const openRepropagate = (poGroup) => {
    const seen = new Map();
    poGroup.batches.forEach(({ list }) => {
      list.forEach(e => {
        if (!seen.has(e.product_id) || e.base_capital > seen.get(e.product_id).base_capital) {
          seen.set(e.product_id, e);
        }
      });
    });
    const reportProducts = Array.from(seen.values()).map(e => ({
      product_id: e.product_id,
      name: e.product_name,
      sku: e.product_sku || '',
      po_unit_price: e.base_capital,
      new_capital: e.base_capital,
      method: 'last_purchase',
    }));
    const sourceBranchId = poGroup.batches[0]?.list[0]?.source_branch_id || '';
    setRepropDialog({ ref: poGroup.ref, sourceBranchId, reportProducts });
  };

  /**
   * 3-level hierarchy:
   *   poGroups → productBatches → individual entries
   *
   * PO group key = source_ref (PO number) or "manual" for propagations without a PO.
   * Within each PO group, entries are sub-grouped by propagation_batch_id (one product → N branches).
   */
  const poGroups = useMemo(() => {
    // Step 1: group entries by source_ref
    const refMap = new Map();
    entries.forEach(e => {
      const ref = e.source_ref && e.source_ref !== 'propagation' ? e.source_ref : 'Manual';
      if (!refMap.has(ref)) refMap.set(ref, []);
      refMap.get(ref).push(e);
    });

    // Step 2: within each ref group, sub-group by propagation_batch_id
    return Array.from(refMap.entries()).map(([ref, refEntries]) => {
      const batchMap = new Map();
      refEntries.forEach(e => {
        const bkey = e.propagation_batch_id || `solo-${e.id}`;
        if (!batchMap.has(bkey)) batchMap.set(bkey, []);
        batchMap.get(bkey).push(e);
      });

      const batches = Array.from(batchMap.entries()).map(([bkey, list]) => ({ bkey, list }));
      const totalBranches = refEntries.length;
      const latestAt = refEntries.reduce((acc, e) => (!acc || e.changed_at > acc ? e.changed_at : acc), null);
      const isPo = ref !== 'Manual' && ref.startsWith('PO');

      return { ref, batches, totalBranches, latestAt, isPo };
    });
  }, [entries]);

  const toggleCollapse = (ref) => setCollapsed(p => ({ ...p, [ref]: !p[ref] }));

  return (
    <div className="space-y-3 mt-4" data-testid="propagation-history-panel">
      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5">
          <GitBranch size={15} className="text-[#1A4D2E]" />
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="h-8 w-52 text-xs" data-testid="prop-branch-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Branches</SelectItem>
              {(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="ghost" className="h-8" onClick={load} data-testid="prop-refresh-btn">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </Button>

        {isAdmin && entries.length > 0 && (
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-[11px] text-slate-400">Undo:</span>
            <Button size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => gate({ type: 'window', since: sinceLastHour() })} data-testid="undo-last-hour-btn">
              <Clock size={12} className="mr-1" /> Last hour
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => gate({ type: 'window', since: sinceToday() })} data-testid="undo-today-btn">
              Today
            </Button>
          </div>
        )}
      </div>

      <ScrollArea className="h-[460px] rounded-lg border" style={{ borderColor: 'var(--border-default)' }}>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
            <RefreshCw size={15} className="animate-spin mr-2" /> Loading…
          </div>
        ) : poGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <History size={28} className="opacity-40" />
            No propagated capital {branchId !== '__all__' ? 'for this branch' : 'yet'}.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border-default)' }}>
            {poGroups.map(({ ref, batches, totalBranches, latestAt, isPo }) => {
              const isCollapsed = collapsed[ref];
              const productCount = batches.length;

              return (
                <div key={ref} data-testid={`prop-po-group-${ref}`}>
                  {/* ── Level 1: PO / source group header ── */}
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 sticky top-0 z-10">
                    <button
                      className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
                      onClick={() => toggleCollapse(ref)}
                      data-testid={`prop-po-toggle-${ref}`}
                    >
                      {isCollapsed
                        ? <ChevronRight size={14} className="text-slate-400 shrink-0" />
                        : <ChevronDown size={14} className="text-slate-400 shrink-0" />
                      }
                      <Package size={14} className={`shrink-0 ${isPo ? 'text-[#1A4D2E]' : 'text-slate-400'}`} />
                      <span className="font-mono font-semibold text-slate-800 text-sm truncate">{ref}</span>
                      <Badge variant="outline" className="text-[10px] shrink-0 ml-1">
                        {productCount} product{productCount !== 1 ? 's' : ''}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        {totalBranches} branch target{totalBranches !== 1 ? 's' : ''}
                      </Badge>
                      <span className="text-[10px] text-slate-400 ml-1 shrink-0">{fmtDateTime(latestAt)}</span>
                    </button>

                    {/* "Undo all from this PO" + "Re-propagate" — only for PO-sourced groups */}
                    {isAdmin && isPo && (
                      <>
                        <Button size="sm" variant="outline"
                          className="h-7 text-[11px] text-[#1A4D2E] border-emerald-200 hover:bg-emerald-50 shrink-0"
                          onClick={() => openRepropagate({ ref, batches })}
                          data-testid={`prop-repropagate-${ref}`}>
                          <GitMerge size={11} className="mr-1" /> Re-propagate
                        </Button>
                        <Button size="sm" variant="outline"
                          className="h-7 text-[11px] text-red-600 border-red-200 hover:bg-red-50 shrink-0"
                          onClick={() => gate({ type: 'po', sourceRef: ref })}
                          data-testid={`prop-undo-po-${ref}`}>
                          <Undo2 size={11} className="mr-1" /> Undo all
                        </Button>
                      </>
                    )}
                  </div>

                  {/* ── Level 2 + 3: product batches (collapsed when header is toggled) ── */}
                  {!isCollapsed && batches.map(({ bkey, list }) => {
                    const head = list[0];
                    const isSet = list.length > 1 && head.propagation_batch_id;

                    return (
                      <div key={bkey} data-testid={`prop-batch-${bkey}`} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                        {/* Level 2: product batch header */}
                        {isSet && (
                          <div className="flex items-center gap-2 px-4 py-1.5 bg-white">
                            <Layers size={12} className="text-slate-400 shrink-0" />
                            <span className="text-[11px] font-semibold text-slate-600 truncate flex-1">
                              {head.product_name}
                            </span>
                            <span className="text-[10px] text-slate-400 shrink-0">
                              {list.length} branches · {fmtDateTime(head.changed_at)}
                            </span>
                            {isAdmin && (
                              <Button size="sm" variant="outline"
                                className="h-6 text-[10px] text-red-600 border-red-200 hover:bg-red-50 shrink-0 ml-1"
                                onClick={() => gate({ type: 'batch', batchId: head.propagation_batch_id })}
                                data-testid={`prop-undo-set-${bkey}`}>
                                <Undo2 size={10} className="mr-1" /> Undo set
                              </Button>
                            )}
                          </div>
                        )}

                        {/* Level 3: individual branch entries */}
                        {list.map(e => (
                          <div key={e.id}
                            className="flex items-center gap-3 px-5 py-2 hover:bg-slate-50/60 border-t"
                            style={{ borderColor: 'var(--border-default)' }}
                            data-testid={`prop-entry-${e.id}`}>
                            <div className="flex-1 min-w-0">
                              {!isSet && (
                                <div className="text-sm font-semibold text-slate-800 truncate">{e.product_name}</div>
                              )}
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge variant="outline" className="text-[10px]">{e.branch_name}</Badge>
                                <span className="text-[10px] text-slate-400 truncate">{e.note}</span>
                              </div>
                              <div className="text-[10px] text-slate-400 mt-0.5">{fmtDateTime(e.changed_at)} · {e.changed_by_name || '—'}</div>
                            </div>
                            <div className="text-right shrink-0" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              <div className="text-[10px] text-slate-400">{formatPHP(e.old_capital)} →</div>
                              <div className="text-sm font-bold text-[#1A4D2E]">{formatPHP(e.new_capital)}</div>
                            </div>
                            {isAdmin && (
                              <Button size="sm" variant="outline"
                                className="h-7 text-[11px] text-red-600 border-red-200 hover:bg-red-50 shrink-0"
                                onClick={() => gate({ type: 'one', changeId: e.id })}
                                data-testid={`prop-undo-${e.id}`}>
                                <Undo2 size={12} className="mr-1" /> Undo
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      <AuthDialog
        open={authOpen}
        onClose={() => { setAuthOpen(false); setPendingAction(null); }}
        mode="either"
        context="cross_branch_capital_propagation"
        onVerified={() => { setAuthOpen(false); if (pendingAction) runAction(pendingAction); setPendingAction(null); }}
      />

      {/* Re-propagate dialog — opens Branch Matrix pre-filled with this PO's products */}
      <Dialog open={!!repropDialog} onOpenChange={(o) => { if (!o) setRepropDialog(null); }}>
        <DialogContent className="max-w-6xl" data-testid="repropagate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]" style={{ fontFamily: 'Manrope' }}>
              <GitMerge size={18} /> Re-propagate from {repropDialog?.ref}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {repropDialog?.reportProducts?.length} product{repropDialog?.reportProducts?.length !== 1 ? 's' : ''} from the original propagation.
              Tick any branch to push the same PO cost again — useful for branches missed the first time
              or after an undo. Previously propagated branches show their current capital for comparison.
            </DialogDescription>
          </DialogHeader>
          {repropDialog && (
            <POBranchPropagateMatrix
              products={repropDialog.reportProducts}
              sourcePoNumber={repropDialog.ref}
              sourceBranchId={repropDialog.sourceBranchId}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
